import { Suspense } from "react"
import { ProductDetails, ProductGallery } from "@/components/organisms"
import { listSellerProducts } from "@/lib/data/products"
import { getProductByHandle } from "@/lib/data/product-by-handle"
import { HomeProductSection } from "../HomeProductSection/HomeProductSection"
import NotFound from "@/app/not-found"
import { getTranslate } from "@/tolgee/server"
import Analytics from "@/components/molecules/Analytics/Analytics"
import { ProductReview } from "@/components/molecules/ProductReview/ProductReview"
import { buildSelectedVariantOptions, resolveVariantId } from "@/lib/utils"
import { Track } from "@/components/molecules/Track/Track"
import { getStorefrontPresentation } from "@/lib/data/sales-channes"
import { isBarebonesTheme } from "@/lib/theme/storefront-theme"
import { ensureProductDetailContent } from "@/lib/helpers/ensure-product-detail-content"
import { BarebonesProductDetailsPage } from "@/components/skins/barebones/BarebonesProductDetailsPage"

async function MoreFromSeller({
  sellerHandle,
  locale,
  heading,
}: {
  sellerHandle: string
  locale: string
  heading: string
}) {
  const sellerProducts = await listSellerProducts({
    handle: sellerHandle,
    query: {
      limit: 18,
      // Do not request *variants.calculated_price — seller products API has no
      // region/currency pricing context and throws calculatePrices errors.
      fields: "*",
    },
    countryCode: locale,
  })

  return (
    <HomeProductSection
      heading={heading}
      products={sellerProducts.products}
      seller_handle={sellerHandle}
      locale={locale}
    />
  )
}

export const ProductDetailsPage = async ({
  handle,
  locale,
  searchParams,
}: {
  handle: string
  locale: string
  searchParams: any
}) => {
  const [t, prod, presentation] = await Promise.all([
    getTranslate(),
    getProductByHandle(handle, locale),
    getStorefrontPresentation(locale),
  ])

  if (!prod) return null

  if (prod.seller?.store_status === "SUSPENDED") {
    return NotFound()
  }

  const barebones = isBarebonesTheme(presentation.theme)
  const product = ensureProductDetailContent(prod)

  const prodVariants = product.variants?.[0] || ({} as any)
  const { currency_code, calculated_amount } =
    prodVariants?.calculated_price || {}

  const trackBlock = (
    <>
      <Track
        event="view_item"
        data={{
          currency: currency_code,
          value: calculated_amount,
          items: [
            {
              item_id: prodVariants.sku,
              item_name: product.title,
            },
          ],
        }}
      />
      <Analytics
        event="view_item"
        data={{
          currency: currency_code,
          value: (calculated_amount || 0) / 100,
          items: JSON.stringify([
            {
              item_id: prodVariants.sku,
              item_name: product.title,
            },
          ]),
        }}
      />
    </>
  )

  if (barebones) {
    return (
      <>
        {trackBlock}
        <BarebonesProductDetailsPage product={product} locale={locale} />
      </>
    )
  }

  const selectedVariant = buildSelectedVariantOptions(product, searchParams)
  const variant = product.variants?.find(
    (v) => v.id === resolveVariantId(product, selectedVariant)
  )

  if (variant?.metadata?.media) {
    product.images?.unshift({
      id: Math.random().toString(),
      url: variant?.metadata?.media as string,
      rank: -1,
    })
  }

  const sellerHandle = product.seller?.handle

  return (
    <>
      {trackBlock}
      <div className="flex flex-col gap-4 py-[60px] md:flex-row md:gap-6 lg:gap-8">
        <div className="w-full md:w-1/2">
          <ProductGallery
            images={
              product?.images?.length
                ? product.images
                : product?.thumbnail
                  ? [
                      {
                        id: "thumbnail",
                        url: product.thumbnail,
                        rank: 0,
                      } as any,
                    ]
                  : []
            }
          />
        </div>
        <div className="w-full md:w-1/2">
          <ProductDetails product={product} locale={locale} />
        </div>
      </div>
      {sellerHandle ? (
        <Suspense
          fallback={
            <div className="h-64 w-full animate-pulse rounded bg-secondary/40" />
          }
        >
          <MoreFromSeller
            sellerHandle={sellerHandle}
            locale={locale}
            heading={t("product_details_page.more_from_this_seller")}
          />
        </Suspense>
      ) : null}
      <div className="py-[40px]">
        <ProductReview product_id={product.id} locale={locale} />
      </div>
    </>
  )
}
